// Part-number and model-number matching helpers for searchParts.
//
// NoCodeBackend can only do case-insensitive substring ("like") filters, so the database call
// fetches candidates using a few spellings of the query, and these helpers then decide which
// candidates really match, ignoring dashes, spaces, slashes and case.

export const PART_COLUMNS = ['oem_pn', 'part_numbr', 'look_up_pn', 'newer'];
export const MODEL_COLUMNS = ['models'];
export const TEXT_COLUMNS = ['descrip1', 'descrip2', 'comments'];
export const ALL_COLUMNS = [...PART_COLUMNS, ...TEXT_COLUMNS, ...MODEL_COLUMNS];

export const SEARCH_MODES = {
  all: ALL_COLUMNS,
  part: PART_COLUMNS,
  model: MODEL_COLUMNS,
};

// Below this length a normalised match is mostly noise ("a1" matching everywhere).
const MIN_NORMALISED = 3;

/** Lowercase and keep letters and digits only: "WB-13 K/10026" -> "wb13k10026". */
export const normalise = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Spellings of one search token to send to the database as "like" filters.
 * - as typed
 * - with separators removed (finds stored "WB13K10026" when the user typed "WB-13K-10026")
 * - its longest letter run and its longest digit run (finds stored "WB-13-K-10026" when the user typed
 *   "WB13K10026"; the candidates are re-checked in code, so this only widens the net)
 */
export function tokenVariants(token) {
  const variants = new Set([token]);
  const collapsed = normalise(token);
  if (collapsed.length >= MIN_NORMALISED) variants.add(collapsed);
  if (collapsed.length >= 6) {
    for (const kind of [/[a-z]+/g, /[0-9]+/g]) {
      const longest = (collapsed.match(kind) || []).sort((a, b) => b.length - a.length)[0];
      if (longest && longest.length >= MIN_NORMALISED) variants.add(longest);
    }
  }
  return [...variants];
}

/** Does `token` appear in `text`, exactly or ignoring separators and case? */
export function textMatches(text, token) {
  const raw = String(text ?? '').toLowerCase();
  if (raw.includes(token.toLowerCase())) return true;
  const wanted = normalise(token);
  return wanted.length >= MIN_NORMALISED && normalise(text).includes(wanted);
}

/** A row matches when every token appears in at least one of the searched columns. */
export function rowMatches(row, tokens, columns) {
  return tokens.every((token) => columns.some((col) => textMatches(row[col], token)));
}

/**
 * Lower score sorts first: exact part number, then part number starting with the query,
 * then part number containing it, then everything else.
 */
export function relevance(row, query) {
  const wanted = normalise(query);
  if (!wanted) return 3;
  let best = 3;
  for (const col of PART_COLUMNS) {
    // Several numbers can share one cell ("WE12X1; 5304"), so test each piece.
    for (const piece of String(row[col] ?? '').split(/[;,\n]+/)) {
      const have = normalise(piece);
      if (!have) continue;
      if (have === wanted) return 0;
      if (have.startsWith(wanted)) best = Math.min(best, 1);
      else if (have.includes(wanted)) best = Math.min(best, 2);
    }
  }
  return best;
}

/**
 * Model numbers from a part's `models` text that match the query, for display (capped).
 * Prefers models containing every token ("wtw5000 dw0" -> WTW5000DW0) and falls back to any token.
 */
export function matchedModels(row, tokens, limit = 5) {
  const wanted = tokens.map(normalise).filter((t) => t.length >= MIN_NORMALISED);
  if (wanted.length === 0) return [];
  const models = String(row.models ?? '').split(/[\s,;]+/).filter((m) => normalise(m).length >= MIN_NORMALISED);
  const pick = (test) => [...new Set(models.filter((m) => test(normalise(m))))].slice(0, limit);
  const strict = pick((have) => wanted.every((t) => have.includes(t)));
  return strict.length > 0 ? strict : pick((have) => wanted.some((t) => have.includes(t)));
}

/** Split a cell holding several part numbers ("WE12X1; 5304") into a de-duplicated list. */
export function splitPartNumbers(value) {
  const seen = new Set();
  const out = [];
  for (const piece of String(value ?? '').split(/[;,\n]+/)) {
    const pn = piece.trim();
    const key = normalise(pn);
    if (key && !seen.has(key)) {
      seen.add(key);
      out.push(pn);
    }
  }
  return out;
}
